import React, { useEffect } from 'react';
import { formatDate, getISODateString } from '../utils/dateUtils';
import { Link } from 'react-router-dom';
import { FaTwitter, FaLinkedin, FaSearch, FaBell, FaGlobe } from 'react-icons/fa';
import SEO from '../components/SEO';

const BlogArticle = () => {
  useEffect(() => {
    // Add structured data for SEO
    const schema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "How to Detect Typosquatting and Cybersquatting Domains to Protect Your Brand",
      "description": "Learn how typosquatting and cybersquatting detection tools protect your business from phishing and brand impersonation.",
      "image": "https://typoguard.io/images/typosquatting-detection-guide.jpg",
      "author": {
        "@type": "Organization",
        "name": "TypoGuard"
      },
      "publisher": {
        "@type": "Organization",
        "name": "TypoGuard",
        "logo": {
          "@type": "ImageObject",
          "url": "https://typoguard.io/logo.png"
        }
      },
      "datePublished": "2026-01-30",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://typoguard.io/blog/typosquatting-detection-guide"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const shareOnTwitter = () => {
    const url = encodeURIComponent('https://typoguard.io/blog/typosquatting-detection-guide');
    const text = encodeURIComponent('How to Detect Typosquatting Domains and Protect Your Brand');
    window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, '_blank');
  };

  const shareOnLinkedIn = () => {
    const url = encodeURIComponent('https://typoguard.io/blog/typosquatting-detection-guide');
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
  };

  return (
    <div className="min-h-screen flex flex-col bg-dark-900 text-dark-50 font-sans">
      <SEO
        title="How to Detect Typosquatting and Cybersquatting Domains to Protect Your Brand"
        description="Learn how typosquatting and cybersquatting detection tools protect your business from phishing and brand impersonation."
        path="/blog/typosquatting-detection-guide"
      />

      {/* Navigation - Optional if you want to reuse Navbar or keep it simple */}
      <nav className="w-full py-6 px-4 md:px-8 border-b border-dark-800 bg-dark-900/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary-400 to-primary-600">TypoGuard</span>
          </Link>
          <div className="space-x-4">
            <Link to="/analysis" className="text-dark-300 hover:text-white transition-colors">Analysis</Link>
            <Link to="/login" className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors">Sign In</Link>
          </div>
        </div>
      </nav>

      <main className="flex-grow container mx-auto px-4 py-8 md:py-12 max-w-7xl relative z-10">
        <article className="glass-panel bg-dark-800/60 backdrop-blur-md rounded-xl shadow-lg border border-white/5 overflow-hidden">

          <div className="p-6 md:p-8">
            {/* Article Header */}
            <header className="mb-8 border-b border-dark-700 pb-8">
              <div className="flex items-center space-x-2 mb-4">
                <span className="px-3 py-1 bg-primary-500/10 text-primary-400 rounded-full text-xs font-semibold uppercase tracking-wider border border-primary-500/20">Cybersecurity Guide</span>
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
                How to Detect Typosquatting and Cybersquatting Domains to Protect Your Brand
              </h1>
              <div className="flex items-center text-dark-400 text-sm mb-6">
                <div className="flex items-center mr-6">
                  <div className="w-8 h-8 rounded-full bg-primary-700 flex items-center justify-center text-white font-bold mr-3">T</div>
                  <span>By TypoGuard Team</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-2">📅</span>
                  <time dateTime="2026-01-30">
                    January 30, 2026
                  </time>
                </div>

                <div className="ml-auto flex space-x-4">
                  <button
                    onClick={shareOnTwitter}
                    className="text-dark-400 hover:text-blue-400 transition-colors"
                    aria-label="Share on Twitter"
                  >
                    <FaTwitter className="text-xl" />
                  </button>
                  <button
                    onClick={shareOnLinkedIn}
                    className="text-dark-400 hover:text-blue-500 transition-colors"
                    aria-label="Share on LinkedIn"
                  >
                    <FaLinkedin className="text-xl" />
                  </button>
                </div>
              </div>
            </header>

            {/* Article Content */}
            <div className="prose prose-invert prose-lg max-w-none text-dark-300">
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-white mb-4 border-l-4 border-primary-500 pl-4">What is Typosquatting and Cybersquatting?</h2>
                <p className="mb-4">
                  Typosquatting, a form of cybersquatting, is also known as URL hijacking. It's a form of cyberattack where malicious actors register domain names that are similar to legitimate websites. While cybersquatting broadly refers to registering domains that infringe on trademarks, typosquatting specifically targets common typing errors.
                </p>
                <p className="mb-4">
                  These domains often contain typos, character transpositions, or different domain extensions. For example, <code className="text-red-400 bg-red-900/20 px-1 rounded">goggle.com</code> instead of <code className="text-primary-400 bg-primary-900/20 px-1 rounded">google.com</code>.
                </p>
                <div className="bg-primary-900/20 border-l-4 border-primary-500 p-6 my-8 rounded-r-lg">
                  <p className="text-primary-100 m-0">
                    <strong className="text-primary-400 block mb-2">Important Takeaway</strong>
                    Cybersquatting and typosquatting affect businesses of all sizes, with attackers registering domains that mimic legitimate brands to deceive users and steal sensitive information.
                  </p>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-bold text-white mb-4 border-l-4 border-blue-500 pl-4">Real-World Examples and Business Risks</h2>
                <p className="mb-6">
                  Typosquatting poses significant risks to businesses of all sizes. Here are some real-world examples:
                </p>
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2 mt-1">⚠️</span>
                    <span><strong className="text-white">Financial Fraud:</strong> Attackers create fake banking websites to steal login credentials.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2 mt-1">⚠️</span>
                    <span><strong className="text-white">Brand Damage:</strong> Counterfeit sites can harm your brand's reputation and customer trust.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2 mt-1">⚠️</span>
                    <span><strong className="text-white">Malware Distribution:</strong> Visitors to these sites may unknowingly download malware.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2 mt-1">⚠️</span>
                    <span><strong className="text-white">Lost Revenue:</strong> Customers may be diverted to competitor sites or fraudulent marketplaces.</span>
                  </li>
                </ul>
              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-bold text-white mb-4 border-l-4 border-primary-500 pl-4">How TypoGuard Helps Detect and Monitor</h2>
                <p className="mb-8">
                  TypoGuard is a powerful <strong className="text-primary-400">typosquatting detection tool</strong> designed to protect your brand from domain impersonation and phishing attacks. Here's how it works:
                </p>

                <div className="grid md:grid-cols-3 gap-6 mb-8 not-prose">
                  <div className="bg-dark-900/50 p-6 rounded-xl border border-dark-700 hover:border-blue-500 transition-colors">
                    <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4 text-blue-400">
                      <FaSearch className="text-xl" />
                    </div>
                    <h3 className="font-bold text-white text-lg mb-2">Comprehensive Scanning</h3>
                    <p className="text-dark-400 text-sm">Our advanced algorithms scan for hundreds of potential typos and variations of your domain.</p>
                  </div>

                  <div className="bg-dark-900/50 p-6 rounded-xl border border-dark-700 hover:border-primary-500 transition-colors">
                    <div className="w-12 h-12 bg-primary-500/20 rounded-lg flex items-center justify-center mb-4 text-primary-400">
                      <FaBell className="text-xl" />
                    </div>
                    <h3 className="font-bold text-white text-lg mb-2">Regular Monitoring</h3>
                    <p className="text-dark-400 text-sm">Get daily or weekly alerts when new suspicious domains are detected through our scheduled scans.</p>
                  </div>

                  <div className="bg-dark-900/50 p-6 rounded-xl border border-dark-700 hover:border-purple-500 transition-colors">
                    <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center mb-4 text-purple-400">
                      <FaGlobe className="text-xl" />
                    </div>
                    <h3 className="font-bold text-white text-lg mb-2">Global Coverage</h3>
                    <p className="text-dark-400 text-sm">Monitor across all major TLDs and international domain extensions.</p>
                  </div>
                </div>


              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-bold text-white mb-4 border-l-4 border-primary-500 pl-4">How to Take Action</h2>
                <p className="mb-6">
                  If you discover a typosquatting domain targeting your brand, here are the steps to take:
                </p>
                <ol className="space-y-4 marker:text-primary-500">
                  <li><strong className="text-white">Document Everything:</strong> Take screenshots and gather evidence of the infringing domain.</li>
                  <li><strong className="text-white">Contact the Registrar:</strong> Most registrars have abuse departments that can help with takedowns.</li>
                  <li><strong className="text-white">File a UDRP Complaint:</strong> For domains that infringe on your trademark.</li>
                  <li><strong className="text-white">Consider Legal Action:</strong> In cases of clear trademark infringement or fraud.</li>

                </ol>
                <div className="mt-8 bg-gradient-to-r from-dark-800 to-dark-700 p-6 rounded-xl border border-dark-600">
                  <h3 className="font-bold text-white mb-2 flex items-center">
                    <span className="text-2xl mr-2">🛡️</span> Pro Tip
                  </h3>
                  <p className="text-dark-300 m-0">
                    Consider registering common typos of your domain proactively to prevent them from being used maliciously. Regular monitoring helps detect new threats early.
                  </p>
                </div>
              </section>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <section className="bg-gradient-to-br from-dark-800 to-dark-900 rounded-2xl p-8 md:p-12 text-center mt-12 border border-dark-700 shadow-2xl relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary-600/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-600/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

          <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white relative z-10">Protect Your Brand Today</h2>
          <p className="text-dark-400 mb-8 max-w-2xl mx-auto relative z-10">
            Start monitoring for typosquatting domains with TypoGuard's free scanning service.
          </p>
          <Link
            to="/login"
            className="inline-block bg-primary-600 hover:bg-primary-700 text-white font-bold py-4 px-10 rounded-full shadow-lg shadow-primary-600/20 transition-all duration-200 transform hover:scale-105 relative z-10"
          >
            Start Free Monitoring
          </Link>
        </section>
      </main>

      {/* Mobile Sticky CTA */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-dark-800 shadow-xl p-4 border-t border-dark-700 z-50">
        <Link
          to="/dashboard"
          className="block w-full bg-primary-600 hover:bg-primary-700 text-white font-bold text-center py-3 px-6 rounded-lg transition-colors duration-200"
        >
          Scan Your Domain Now — Free!
        </Link>
      </div>


    </div>
  );
};

export default BlogArticle;
